// Layer 4 (UI) — the selected document (locked comp
// `.impeccable/mocks/decision/documents.html` B, `.pane`): a header with the
// document's kind, version, publish date and its one [syn] chip, then the body
// shaped per kind — ordered steps divided by hairlines (a safety note is a
// neutral caution line, an interlock a neutral reference), this shift's round
// sheet as a table under its progress, or training sections with the pending
// revision as a tracked insertion. Renders `Scene.transform.ts`; derives nothing.

import { FileText } from 'lucide-react';
import { EmptyState, Pill } from '@/design-system';
import { cn } from '@/shared/cn';
import type { DocumentRecord } from './seed';
import { displayOverrideAuthority, roundSheetProgress, type ProcedureView } from './Scene.transform';
import { CAPS, DOC_ID, DRAFT_PILL, REF, SYN_MARK } from './documents-ui';

type DocumentPaneProps = {
  doc: DocumentRecord | ProcedureView | undefined;
};

export function DocumentPane({ doc }: DocumentPaneProps) {
  if (!doc) {
    return (
      <div className="p-6">
        <EmptyState icon={<FileText className="mx-auto h-8 w-8 text-zinc-400" />} title="No document selected" />
      </div>
    );
  }
  if (doc.kind === 'procedure') return <ProcedureBody doc={doc as ProcedureView} />;
  if (doc.kind === 'round-sheet') return <RoundSheetBody doc={doc} />;
  return <TrainingBody doc={doc} />;
}

function DocHeader({ id, title, meta, version, updatedAt }: {
  id: string;
  title: string;
  meta: string;
  version: string;
  updatedAt: string;
}) {
  return (
    <div className="border-b border-[var(--hairline)] px-5 pb-3 pt-3.5" data-doc-header={id}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={DOC_ID}>{id}</span>
        <h2 className="text-[17px] font-semibold tracking-[-0.01em] text-ink-primary">{title}</h2>
      </div>
      <div className="mt-[3px] flex flex-wrap items-center gap-2 text-xs text-muted">
        <span>{meta}</span>
        <span className={REF}>{version}</span>
        <span>published {updatedAt}</span>
        <span className={SYN_MARK}>[syn]</span>
      </div>
    </div>
  );
}

function CautionGlyph() {
  return (
    <svg viewBox="0 0 12 12" aria-hidden className="mt-0.5 h-3 w-3 shrink-0 text-muted">
      <path d="M6 1.5 11 10.5H1Z" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M6 5v2.6M6 9v.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function ProcedureBody({ doc }: { doc: ProcedureView }) {
  return (
    <>
      <DocHeader
        id={doc.id}
        title={doc.title}
        meta={doc.criticalSop ? `${doc.procedureType} · critical SOP` : doc.procedureType}
        version={doc.version}
        updatedAt={doc.updatedAt}
      />
      <ol className="px-5 pb-3 pt-1">
        {doc.steps.map((step) => (
          <li
            key={step.sequenceNo}
            data-procedure-step={step.sequenceNo}
            className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-2.5 border-b border-[var(--color-divider)] py-2.5 last:border-b-0"
          >
            <span className="grid h-[22px] w-[22px] place-items-center rounded-full border border-[var(--hairline-strong)] bg-[var(--surface-2)] text-[11px] font-semibold tabular-nums text-ink-secondary">
              {step.sequenceNo}
            </span>
            <div className="min-w-0">
              <p className="text-[13px] leading-[1.45] text-ink-primary">{step.instruction}</p>
              {step.safetyNote ? (
                <p className="mt-[5px] flex items-start gap-1.5 text-xs leading-[1.4] text-ink-secondary">
                  <CautionGlyph />
                  <span>{step.safetyNote}</span>
                </p>
              ) : null}
              {step.verification ? (
                <p className="mt-1 text-xs text-ink-secondary">
                  <span className={cn(CAPS, 'mr-1')}>Verify</span>
                  {step.verification}
                </p>
              ) : null}
              {step.interlocks.length > 0 ? (
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {step.interlocks.map((il) => (
                    <span
                      key={il.id}
                      data-interlock={il.id}
                      className="inline-flex max-w-full flex-wrap items-center gap-x-1.5 rounded-md border border-[var(--hairline-strong)] bg-[var(--surface-2)] px-1.5 py-px text-[11px] text-ink-secondary"
                    >
                      <span className="font-mono font-medium text-ink-primary">{il.id}</span>
                      <span>{il.label}</span>
                      <span className="whitespace-nowrap text-muted">· {displayOverrideAuthority(il.overrideAuthority)}</span>
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
    </>
  );
}

function RoundSheetBody({ doc }: { doc: Extract<DocumentRecord, { kind: 'round-sheet' }> }) {
  const progress = roundSheetProgress(doc.id, doc.shiftLabel);
  return (
    <>
      <DocHeader id={doc.id} title={doc.title} meta={`${doc.circuit} · ${doc.shiftLabel}`} version={doc.version} updatedAt={doc.updatedAt} />
      <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 border-b border-[var(--hairline)] px-5 py-2.5 text-[12.5px] tabular-nums" data-round-progress>
        <span>
          <span className="font-semibold text-ink-primary">{progress.done}</span> of {progress.total} done
        </span>
        <span className="grid gap-[3px]" style={{ gridTemplateColumns: `repeat(${doc.tasks.length}, minmax(0, 1fr))` }}>
          {doc.tasks.map((task) => (
            <span
              key={task.id}
              className={cn(
                'h-2 rounded-[2px]',
                task.status === 'flagged'
                  ? 'bg-[var(--state-warning)]'
                  : task.status === 'pending'
                    ? 'border border-dashed border-[var(--hairline-strong)]'
                    : 'bg-ink-secondary',
              )}
            />
          ))}
        </span>
        <span>
          {progress.flagged.length > 0 ? (
            <>
              <span className="font-semibold text-[var(--state-warning)]">{progress.flagged.length} flagged</span> ·{' '}
            </>
          ) : null}
          {progress.pending.length} due
        </span>
      </div>
      <table className="w-full border-collapse text-[12.5px] tabular-nums">
        <thead>
          <tr>
            {['Due', 'Check', 'Good looks like / bad looks like', 'Reading'].map((head, index) => (
              <th
                key={head}
                className={cn(CAPS, 'whitespace-nowrap border-b border-[var(--color-divider)] px-2 py-[7px] text-left', index === 0 ? 'pl-4' : '', index === 3 ? 'pr-4' : '')}
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {doc.tasks.map((task) => (
            <tr key={task.id} data-round-task={task.id} data-status={task.status} className="align-top">
              <td className="whitespace-nowrap border-b border-[var(--color-divider)] px-2 py-2 pl-4 text-ink-primary">
                {task.dueAt}
                <span className="mt-px block text-[11.5px] text-muted">{task.frequency}</span>
              </td>
              <td className="border-b border-[var(--color-divider)] px-2 py-2">
                <span className="font-medium text-ink-primary">{task.check}</span>
                <span className="mt-px block font-mono text-[11px] text-muted">{task.tag}</span>
              </td>
              <td className="border-b border-[var(--color-divider)] px-2 py-2 text-xs leading-[1.4] text-ink-secondary">
                {task.goodLooksLike}
                <span className="block text-muted">{task.badLooksLike}</span>
              </td>
              <td className="whitespace-nowrap border-b border-[var(--color-divider)] px-2 py-2 pr-4">
                {task.status === 'done' ? (
                  <span className="text-ink-primary">{task.reading}</span>
                ) : task.status === 'flagged' ? (
                  <>
                    <span className="font-semibold text-[var(--state-warning)]">{task.reading}</span>
                    <span className="mt-px block text-[11.5px] text-muted">flagged</span>
                  </>
                ) : (
                  <span className="text-muted">due {task.dueAt}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function TrainingBody({ doc }: { doc: Extract<DocumentRecord, { kind: 'training' }> }) {
  return (
    <>
      <DocHeader
        id={doc.id}
        title={doc.title}
        meta={doc.criticalSop ? `${doc.audienceRole} · critical` : doc.audienceRole}
        version={doc.version}
        updatedAt={doc.updatedAt}
      />
      <div className="px-5 pb-3.5 pt-1.5">
        {doc.sections.map((section) => (
          <div key={section.heading}>
            <h3 className="mb-1 mt-3 text-[13px] font-semibold text-ink-primary">{section.heading}</h3>
            <p className="text-[13px] leading-[1.55] text-ink-secondary">{section.body}</p>
          </div>
        ))}

        {doc.pendingRevision ? (
          <div
            data-tracked-insertion={doc.pendingRevision.id}
            className="mt-3.5 rounded-r-[10px] border-l-[3px] border-[var(--state-online)] bg-[color-mix(in_srgb,var(--state-online)_10%,transparent)] px-3.5 py-2.5"
          >
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-[13px] font-semibold text-ink-primary">{doc.pendingRevision.insertion.heading}</h3>
              <span className="flex-1" />
              <Pill className={DRAFT_PILL}>Draft — not live until approved</Pill>
            </div>
            <p className="mt-0.5 text-[11.5px] text-muted">{doc.pendingRevision.insertion.flaggedNote}</p>
            <dl className="mt-2 grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-[12.5px]">
              <dt className={cn(CAPS, 'pt-0.5')}>Symptom</dt>
              <dd className="leading-[1.45] text-ink-primary">{doc.pendingRevision.insertion.symptom}</dd>
              <dt className={cn(CAPS, 'pt-0.5')}>Detection logic</dt>
              <dd className="leading-[1.45] text-ink-primary">{doc.pendingRevision.insertion.detectionLogic}</dd>
              <dt className={cn(CAPS, 'pt-0.5')}>Immediate response</dt>
              <dd className="leading-[1.45] text-ink-primary">{doc.pendingRevision.insertion.immediateResponse}</dd>
            </dl>
          </div>
        ) : null}
      </div>
    </>
  );
}
